import { getLocalDateString, subjectMap } from './constants.js';

// --- HELPERS ---
const getMajorUnit = (hnu) => {
    if (!hnu) return 9999;
    const parts = String(hnu).split('.');
    const val = parseInt(parts[0], 10);
    return isNaN(val) ? 9999 : val;
};

// Standardized safe look-up for subject sequence
const getSequenceIndex = (subject, seqMap) => {
    if (!subject) return 9999;
    const key = subject.toLowerCase().trim();
    return seqMap.has(key) ? seqMap.get(key) : 9999;
};

const getRemainingStudyDays = (startDate, targetDateStr, studyDaysInput, docketSchedule) => {
    if (!targetDateStr) return 1; 
    const iter = new Date(startDate);
    iter.setHours(0,0,0,0);
    const end = new Date(targetDateStr + 'T00:00:00');
    if (end <= iter) return 0;
    const studyDaysOfWeek = new Set((studyDaysInput || []).map(d => d.toLowerCase()));
    const overrideMap = new Map();
    if (Array.isArray(docketSchedule)) {
        docketSchedule.forEach(day => {
            if (typeof day.is_break_day === 'boolean') overrideMap.set(day.date, day.is_break_day);
        });
    }
    let validDays = 0;
    let safety = 0;
    while (iter <= end && safety < 1825) {
        const dateStr = getLocalDateString(iter);
        const dayName = iter.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        let isAvailable = overrideMap.has(dateStr) ? !overrideMap.get(dateStr) : studyDaysOfWeek.has(dayName);
        if (isAvailable) validDays++;
        iter.setDate(iter.getDate() + 1);
        safety++;
    }
    return validDays;
};

const calculateDailyTarget = (userData, totalUnlearned) => {
    const baseTarget = parseInt(userData.goals?.daily_rule_target || 5, 10);
    const targetDateStr = userData.goals?.target_mastery_date;
    
    if (!targetDateStr || totalUnlearned <= 0) return baseTarget;

    // Calculate available days remaining (including today if it's not a break)
    const availableDays = getRemainingStudyDays(
        new Date(), 
        targetDateStr, 
        userData.goals?.study_days, 
        userData.docket?.schedule
    );

    // If we have very few days left, availableDays might be 1 or 2, 
    // driving the target up to your "29" catch-up number.
    if (availableDays <= 0) return Math.min(100, totalUnlearned);
    
    const calculated = Math.ceil(totalUnlearned / availableDays);
    
    // Ensure we don't drop to 5 if the math says we need 29 to finish on time
    return Math.max(baseTarget, calculated);
};

export const getSortedUnlearnedRules = ({ userData, allItemsMap }) => {
    // 1. Convert Map to Array
    const rules = [...allItemsMap.values()].filter(item => item.itemType === 'rule');
    
    // 2. Get User Preferences
    const seq = userData.diagnostics?.subject_sequence || 
                userData.profile?.preferences?.subject_sequence || 
                Object.keys(subjectMap);
                
    const excluded = new Set(userData.goals?.excluded_subjects || []);
    const hfrRestricted = new Set(userData.goals?.hfr_restricted_subjects || []);
    const fastTrackSubjects = new Set(userData.goals?.denovo_track_subjects || []);
    const isMixMode = userData.goals?.mix_mode || false;
    const userAccessLevel = userData.access_level || 'free';

    // 3. Filter Candidates
    let potential = rules.filter(r => {
        const progress = userData.rulesProgress?.[r.id];
        
        // Skip if already learned
        if (progress?.srs_data?.lastReviewedDate) return false;
        
        // --- THE FIX: STRICTLY ENFORCE FREE PREVIEW ---
        if (userAccessLevel === 'free' && !r.is_free_preview) return false;
        // ----------------------------------------------

        // Must not be excluded
        if (excluded.has(r.subject)) return false;
        // Must not be Fast Track
        if (fastTrackSubjects.has(r.subject)) return false; 
        // Must respect HFR filters
        const isHFR = r.isHFR || r.is_hfr;
        if (hfrRestricted.has(r.subject) && !isHFR) return false;
        
        return true;
    });

    // 4. Create Normalized Sequence Map
    const seqMap = new Map(seq.map((s, i) => [s.toLowerCase().trim(), i]));

    // 5. Sort
    potential.sort((a, b) => {
        // A. Primary Sort: User Sequence (or Mixed Unit)
        if (isMixMode) {
            const unitA = getMajorUnit(a.HNU), unitB = getMajorUnit(b.HNU);
            if (unitA !== unitB) return unitA - unitB;
        } else {
            const idxA = getSequenceIndex(a.subject, seqMap);
            const idxB = getSequenceIndex(b.subject, seqMap);
            if (idxA !== idxB) return idxA - idxB;
        }

        // B. Secondary Sort: Logical HNU Order
        const hnuA = a.HNU || '', hnuB = b.HNU || '';
        return hnuA.localeCompare(hnuB, undefined, { numeric: true }) || a.id.localeCompare(b.id);
    });

    // 6. Linear Mode Enforcement
    if (!isMixMode && potential.length > 0) {
        const currentSubject = potential[0].subject;
        return potential.filter(r => r.subject === currentSubject);
    }

    return potential;
};

export const generateProjectedSchedule = ({ userData, allItemsMap }) => {
    const projectionMap = new Map();
    
    // 1. Get Settings & Normalization
    const rawSeq = userData.diagnostics?.subject_sequence || 
                   userData.profile?.preferences?.subject_sequence || 
                   [];
    
    // Normalize user sequence to lowercase IDs (e.g., "Torts" -> "torts")
    const userSeq = rawSeq.map(s => s.toLowerCase().trim());

    const excluded = new Set(userData.goals?.excluded_subjects || []);
    const hfrRestricted = new Set(userData.goals?.hfr_restricted_subjects || []);
    const fastTrackSubjects = new Set(userData.goals?.denovo_track_subjects || []); // These shouldn't block the linear queue
    const isMixMode = userData.goals?.mix_mode || false;
    const studyDaysOfWeek = new Set((userData.goals?.study_days || []).map(d => d.toLowerCase()));
    
    // Hard Deadlines
    const targetMasteryDate = userData.goals?.target_mastery_date;
    const barExamDate = userData.goals?.bar_exam_date;

    // 2. Build Docket Exclusion Map (Holidays/Breaks)
    const docketMap = new Map();
    (userData.docket?.schedule || []).forEach(d => { 
        if (typeof d.is_break_day === 'boolean') docketMap.set(d.date, d.is_break_day); 
    });

    // 3. Count "Work Remaining"
    // We only count rules that actually require a "Learn" slot. 
    // We exclude Fast Track (they are learned in Review) and Excluded subjects.
    const counts = {};
    const subjectsWithWork = new Set();
    let totalUnlearned = 0;
    
    [...allItemsMap.values()].forEach(item => {
        if (item.itemType !== 'rule') return;
        
        // Skip if already learned
        if (userData.rulesProgress?.[item.id]?.srs_data?.lastReviewedDate) return;
        
        // Skip based on User Strategy
        if (excluded.has(item.subject)) return;
        if (fastTrackSubjects.has(item.subject)) return; // Don't project these in the linear timeline
        
        // Skip if HFR Restricted (User only wants to see HFRs for this subject)
        const isHFR = item.isHFR || item.is_hfr;
        if (hfrRestricted.has(item.subject) && !isHFR) return;

        counts[item.subject] = (counts[item.subject] || 0) + 1;
        totalUnlearned++;
        subjectsWithWork.add(item.subject);
    });

    // 4. Construct Effective Sequence
    // We prioritize the User's Sequence, but only include subjects that actually have work remaining.
    // Any subject with work that ISN'T in the user's sequence is appended to the end.
    let effectiveSeq = [
        ...userSeq.filter(s => subjectsWithWork.has(s)), 
        ...Array.from(subjectsWithWork).filter(s => !userSeq.includes(s))
    ];

    const dailyTarget = calculateDailyTarget(userData, totalUnlearned);
    
    // 5. Projection Loop
    let currentDate = new Date(); 
    currentDate.setHours(0,0,0,0); 
    
    let dayCounter = 0;
    let seqIndex = 0;
    let remaining = totalUnlearned;

    while (dayCounter < 365) {
        const dateStr = getLocalDateString(currentDate);
        
        if (barExamDate && dateStr > barExamDate) break; 
        if (targetMasteryDate && dateStr > targetMasteryDate) break;

        // Check availability (Docket override OR Weekly preference)
        const dayName = currentDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        let isBreak = docketMap.has(dateStr) 
            ? docketMap.get(dateStr) 
            : !studyDaysOfWeek.has(dayName);
        
        // If we are out of work, every day becomes a "break" (or review day)
        const canAssignRules = !isBreak && remaining > 0;

        if (canAssignRules) {
            // Determine Subject
            let subjectForDay = 'mixed';
            
            if (!isMixMode) {
                // LINEAR MODE: Stick to current subject until exhausted
                const currentSubject = effectiveSeq[seqIndex];
                subjectForDay = currentSubject;
                
                // Decrement count
                const volume = Math.min(dailyTarget, counts[currentSubject]);
                counts[currentSubject] -= volume;
                remaining -= volume;

                // Move to next subject if finished
                if (counts[currentSubject] <= 0) {
                    seqIndex++;
                }
            } else {
                // MIX MODE: Just burn down the total
                remaining -= Math.min(dailyTarget, remaining);
            }
            
            projectionMap.set(dateStr, { 
                date: dateStr, 
                isBreak: false, 
                projectedSubject: subjectForDay, 
                volume: Math.min(dailyTarget, remaining + dailyTarget) // Approx volume
            });

            if (!isMixMode && seqIndex >= effectiveSeq.length) remaining = 0;

        } else {
            projectionMap.set(dateStr, { 
                date: dateStr, 
                isBreak: true, 
                volume: 0,
                projectedSubject: null 
            });
        }

        currentDate.setDate(currentDate.getDate() + 1);
        dayCounter++;
    }
    return projectionMap;
};


export const generateCurriculum = ({ userData, canonicalData }) => {
    const today = new Date();
    const todayStr = getLocalDateString(today);
    const dayName = today.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
    
    const schedule = Array.isArray(userData.docket?.schedule) ? userData.docket.schedule : [];
    
    const todayEntry = schedule.find(day => day.date === todayStr);
    const studyDays = new Set((userData.goals?.study_days || []).map(d => d.toLowerCase()));
    
    let isBreak = !studyDays.has(dayName); 
    if (todayEntry && typeof todayEntry.is_break_day === 'boolean') {
        isBreak = todayEntry.is_break_day; 
    }

    const excluded = new Set(userData.goals?.excluded_subjects || []);
    const hfrRestricted = new Set(userData.goals?.hfr_restricted_subjects || []);
    const fastTrackSubjects = new Set(userData.goals?.denovo_track_subjects || []);

let sortedPool = [];
    let totalUnlearnedScopeCount = 0;
    const isMixMode = userData.goals?.mix_mode || false;

    if (canonicalData?.rules) {
        const seq = userData.diagnostics?.subject_sequence || [];
        const seqMap = new Map(seq.map((s, i) => [s, i]));
        
        sortedPool = [...canonicalData.rules].filter(rule => {
            const progress = userData.rulesProgress?.[rule.id];
            const isStarted = !!progress?.srs_data?.lastReviewedDate;
            if (isStarted || excluded.has(rule.subject)) return false;
            if (hfrRestricted.has(rule.subject) && !(rule.isHFR || rule.is_hfr)) return false;
            
            totalUnlearnedScopeCount++;
            return true;
        }).sort((a, b) => {
            const idxA = seqMap.get(a.subject) ?? 999;
            const idxB = seqMap.get(b.subject) ?? 999;
            if (idxA !== idxB) return idxA - idxB;
            return (a.HNU || '').localeCompare(b.HNU || '', undefined, { numeric: true });
        });
    }

    const dailyTarget = calculateDailyTarget(userData, totalUnlearnedScopeCount);
    const learnedToday = todayEntry?.metrics?.learned_count || 0;
    const remainingQuota = Math.max(0, dailyTarget - learnedToday);

    // 1. Filter pool based on Linear vs Mix Mode
    let dailyCandidatePool = [];
    if (isMixMode) {
        dailyCandidatePool = sortedPool;
    } else if (sortedPool.length > 0) {
        const currentSubject = sortedPool[0].subject;
        dailyCandidatePool = sortedPool.filter(r => r.subject === currentSubject);
    }

    // 2. Take top items up to quota
    const selectedItems = dailyCandidatePool.slice(0, remainingQuota);

    // 3. Distribute into queues with 2/3 Fast Track limit
    const fastTrackCap = Math.floor(remainingQuota * (2/3));
    let learnQueue = [];
    let fastTrackQueue = [];

    selectedItems.forEach(rule => {
        const isFastTrackSubject = fastTrackSubjects.has(rule.subject);
        if (isFastTrackSubject && fastTrackQueue.length < fastTrackCap) {
            fastTrackQueue.push(rule.id);
        } else {
            learnQueue.push(rule.id);
        }
    });

    const queues = { learn: learnQueue, fast_track: fastTrackQueue };

    const updatedTodayEntry = {
        date: todayStr,
        is_break_day: isBreak,
        targets: { 
            learn: isBreak ? 0 : dailyTarget, 
            quiz: isBreak ? 0 : 3
        },
        metrics: todayEntry?.metrics ? { ...todayEntry.metrics } : { learned_count: 0, quiz_count: 0 },
        queues: isBreak ? { learn: [], fast_track: [] } : queues
    };

    let newSchedule = schedule.filter(day => day.date !== todayStr);
    newSchedule.push(updatedTodayEntry);
    newSchedule.sort((a, b) => a.date.localeCompare(b.date));

    return { schedule: newSchedule };
};